// How the Auth0 SDK gets and keeps tokens — the first thing every page load
// waits on.
//
// The SDK cache in localStorage, everywhere: a reload takes the access token
// straight from the cache — no network before the app's first request. The
// in-memory cache production used before started every page load with a
// hidden-iframe /authorize round trip (0.5–1.3 s measured) before anything
// else could run.
//
// Refresh tokens are asked for (offline_access) but, as of 2026-09, the rail
// API does not allow offline access, so Auth0 grants "openid profile email"
// only and the SDK renews through the silent-auth iframe instead. Access
// tokens for the API live 30 days. Allowing offline access on the API in
// Auth0 would move renewals to one POST /oauth/token with no code change —
// BUT the refresh-token grant does not consult the Auth0 session, so
// FreshTokenOnLoad would stop noticing an ended session (logged out
// elsewhere, sessions revoked, session lifetime over): a refresh token is
// bounded only by its own inactivity / absolute lifetimes. Enable it together
// with rotation + reuse detection and a short absolute lifetime, or keep a
// session check.
//
// The trade-off: tokens in localStorage are readable by script running on the
// page, so an XSS bug could lift one (valid up to its 30 days; a refresh
// token, once issued, is bounded by rotation with reuse detection).
//
// Claims and sessions: a cached access token keeps the claims it was minted
// with, and outlives the Auth0 session. FreshTokenOnLoad (below the provider)
// renews it once per page load shortly after boot — re-running the
// post-login Action, so Auth0 console changes to claims reach the app on the
// next load as before — and sends the user to sign in when Auth0 reports the
// session gone, as a reload used to.
//
// Fallback to the iframe when there is no usable refresh token (today: always;
// later, a revoked or expired one): production only. On
// localhost the iframe hits Auth0's "Non-Verifiable Callback URI End-User
// Confirmation" tenant setting, which forces a consent screen that
// prompt=none cannot render — getAccessTokenSilently would return
// consent_required and useAccessToken loop via loginWithRedirect — so there a
// missing refresh token goes straight to the login redirect instead.
export const auth0TokenOptions = ({ isDev }: { isDev: boolean }) =>
  ({
    useRefreshTokens: true,
    useRefreshTokensFallback: !isDev,
    cacheLocation: "localstorage",
  }) as const

// offline_access: the scope that makes Auth0 issue the refresh token
export const AUTH0_SCOPE = "openid profile email offline_access"

// After the boot requests: the SDK shares one in-flight token promise between
// every getAccessTokenSilently call (whatever its cacheMode), so a renewal at
// boot would hold the first page requests behind it.
export const FRESH_TOKEN_DELAY_MS = 5_000
// the SDK's default silent-auth timeout is 60 s
export const FRESH_TOKEN_TIMEOUT_S = 10
